import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock, X, Sparkles } from 'lucide-react';
import { AppUser } from '../types';
import { getTrialStatus } from '../utils/trialStatus';
import { isAdminUser, isActiveTester } from '../services/accessControl';

/**
 * Prøveslutt-banner (#6A ærlig tapsramme — se docs/plan-ux-principles-retention.md).
 * Vises KUN til prøve-brukere nær slutt som ikke allerede betaler. Sann og hjelpsom,
 * ikke et mørkt mønster: ingen falsk knapphet, avvisbar for dagen, forsvinner når prøven
 * er over eller brukeren oppgraderer. Innenfor gissel-grensen — påpeker bare at premium-
 * bekvemmelighetene forsvinner ved prøveslutt, holder ingenting brukeren har skapt som gissel.
 */

const DISMISS_KEY = 'fricamp_trial_banner_dismissed';

function todayKey(): string {
  // Lokal dato holder — banneren skal maks vises én gang per dag.
  return new Date().toISOString().split('T')[0];
}

function isRealSubscriber(user: AppUser): boolean {
  return user.subscriptionStatus === 'paid'
    || (typeof user.paidUntil === 'number' && Date.now() < user.paidUntil);
}

interface TrialExpiryBannerProps {
  user: AppUser;
  onUpgrade: () => void;
}

const TrialExpiryBanner: React.FC<TrialExpiryBannerProps> = ({ user, onUpgrade }) => {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(DISMISS_KEY) === todayKey();
    } catch {
      return false;
    }
  });

  const trial = getTrialStatus(user.creationTimestamp, Date.now());

  // Gate: prøven må være i ferd med å utløpe, brukeren betaler ikke allerede, og er
  // ikke admin/tester (de har uansett tilgang). Avvist for dagen → skjult.
  const shouldShow =
    !dismissed &&
    trial.isExpiringSoon &&
    !isRealSubscriber(user) &&
    !isAdminUser(user) &&
    !isActiveTester(user);

  if (!shouldShow) return null;

  const handleDismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, todayKey()); } catch { /* ignore */ }
    setDismissed(true);
  };

  return (
    <div className="fixed left-1/2 -translate-x-1/2 bottom-24 z-[4000] w-[min(94vw,420px)] animate-in slide-in-from-bottom-4 fade-in duration-300">
      <div className="relative overflow-hidden rounded-3xl border border-amber-500/25 bg-[#151210]/95 shadow-[0_24px_70px_rgba(0,0,0,0.45)] backdrop-blur-xl">
        <div className="relative p-4 flex items-start gap-3">
          <div className="w-10 h-10 rounded-2xl flex items-center justify-center border border-amber-400/30 bg-amber-400/10 text-amber-300 shrink-0">
            <Clock size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[14px] font-black text-[var(--text-primary)]">
              {t('trial.expiring_title', 'Prøveperioden utløper snart')}
            </p>
            <p className="mt-0.5 text-tiny text-[var(--text-secondary)] leading-relaxed">
              {t('trial.expiring_body', 'Du har {{count}} dager igjen. Behold premium-funksjonene ved å oppgradere.', { count: trial.daysLeft })}
            </p>
            <button
              type="button"
              onClick={onUpgrade}
              className="mt-2.5 inline-flex items-center gap-1.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-black px-3 py-1.5 text-2xs font-black uppercase tracking-widest transition-colors"
            >
              <Sparkles size={12} /> {t('trial.expiring_cta', 'Se muligheter')}
            </button>
          </div>
          <button
            type="button"
            onClick={handleDismiss}
            aria-label={t('common.close', 'Lukk')}
            className="p-2 rounded-full text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-white/5 transition-colors shrink-0"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default TrialExpiryBanner;
